import { observer } from 'mobx-react-lite'
import { Alert, Box, Button, Icon, Typography } from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { type IResponseNumber } from 'entities/Phone/api/types'
import { type NumbersStore } from 'pages/settings/pages/inboxes/pages/id/store/numbersStore'
import styles from './styles.module.scss'

type IRemoveNumberModalContentProps = {
  store: NumbersStore
  number: IResponseNumber
}

export const RemoveNumberModalContent = observer(
  ({ store, number }: IRemoveNumberModalContentProps) => {
    const {
      loadingAssignedNumbers,
      selectedReassignNumber,
      closeModal,
      removeNumber,
      loadingRemoveNumber,
      availableNumbersForReassign,
      numberSearch,
      setNumberSearch,
    } = store

    const renderSelectedNumber = () => {
      if (!selectedReassignNumber) return 'Select number'

      const inboxName = selectedReassignNumber.numberable?.name

      return (
        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
          <Typography
            className={styles.selectedInboxName}
            variant='body-md-regular'
            ellipsis
            ariaLabel='selected-inbox-name'
          >
            {inboxName}
          </Typography>
          <Icon icon={'ellipse_6'} fontSize={2} color={'var(--content-neutral-primary)'} />
          <Typography
            variant='body-md-regular'
            color='var(--content-primary-tertiary)'
            ariaLabel='selected-number'
          >
            {selectedReassignNumber.formatted_number}
          </Typography>
        </Box>
      )
    }

    return (
      <div>
        <Alert
          item={{
            type: 'alert',
            desc: `Remove ${number.formattedNumber} from inbox?`,
          }}
          marginBottom={24}
        />
        <SelectField
          labelText={'Reassign conversations to:'}
          value={selectedReassignNumber?.id}
          loading={loadingAssignedNumbers}
          setValue={(id) => {
            const num = availableNumbersForReassign.find((n) => n.id === id)
            if (num) {
              store.setSelectedReassignNumber(num)
            }
          }}
          items={availableNumbersForReassign.map((num) => {
            const inboxName = num.numberable?.name
            return {
              id: num.id,
              label: num.formatted_number,
              noRenderEmptyActiveValue: true,
              labelRight: (
                <div className={styles.labelRightWrapper}>
                  <Typography
                    variant={'body-md-regular'}
                    ellipsis
                    tooltipProps={{ label: inboxName }}
                    ariaLabel={'inboxName'}
                    color={'tertiary'}
                  >
                    {inboxName}
                  </Typography>
                </div>
              ),
            }
          })}
          selectProps={{
            placeholder: 'Select number',
          }}
          dropdownProps={{
            propsDropdownList: {
              width: 420,
              textFieldProps: {
                value: numberSearch,
                onChange: setNumberSearch,
              },
            },
          }}
          withSearch={true}
          size={'medium'}
          btnProps={{
            placeholder: 'Select number',
            text: renderSelectedNumber(),
            textIcon: selectedReassignNumber ? 'inboxOpen' : undefined,
            textIconColor: 'var(--content-primary-tertiary)',
            style: {
              height: '40px',
              color: !selectedReassignNumber ? 'var(--content-neutral-primary)' : undefined,
            },
          }}
        />
        <div className={styles.actions}>
          <Button text='Cancel' contained='tertiary' size='medium' onClick={closeModal} />
          <Button
            text='Remove number'
            typeBtn='contained'
            size='medium'
            loading={loadingRemoveNumber}
            disabled={!selectedReassignNumber}
            onClick={() => removeNumber(number.id)}
          />
        </div>
      </div>
    )
  }
)
